import type { AlertColor } from '@mui/material';
import { createElement, FocusEvent, KeyboardEvent, MouseEvent, ReactElement } from 'react';
import { AppDispatch } from '../redux/store';
import { updateCard } from '../redux/trip/trip.slice';
import type { TripCard } from '../redux/trip/trip.types';

export type Card = TripCard;

export interface CardDetailsProps {
  card: Card;
  tripId: string;
  canEdit?: boolean;
  open?: boolean;
  listLabel?: string;
  handleClose?: () => void;
  handleDeleteClick?: (event: MouseEvent<EventTarget>) => void;
  dispatch?: AppDispatch;
  setSnackbarMessage?: (message: { severity: AlertColor; text: string } | null) => void;
  handleGalleryClose?: () => void;
}

export const hasValidRating = (rating?: number): boolean =>
  Boolean(rating !== undefined && Number.isFinite(rating) && rating > 0);

export const getStaticMapUrl = (
  card: Card,
  options?: {
    zoom?: number;
    size?: { width: number; height: number };
    scale?: number;
    mapId?: string;
    markerLabel?: string;
    isMobile?: boolean;
  }
): string => {
  if (!card?.location?.latitude || !card?.location?.longitude) {
    return '';
  }

  const {
    zoom = 15,
    size = { width: 600, height: 300 },
    scale = 2,
    mapId,
    markerLabel = '',
    isMobile = false,
  } = options || {};

  const marker = markerLabel
    ? `markers=size:${isMobile ? 'mid' : 'lrg'}%7Ccolor:red%7Clabel:${markerLabel}%7C`
    : `markers=size:${isMobile ? 'mid' : 'lrg'}%7Ccolor:red%7C`;

  const mapIdParam = mapId ? `&map_id=${mapId}` : '';

  return `https://maps.googleapis.com/maps/api/staticmap?center=${card.location.latitude},${
    card.location.longitude
  }&zoom=${zoom}&size=${size.width}x${size.height}&scale=${scale}&maptype=roadmap&${marker}${
    card.location.latitude
  },${card.location.longitude}${mapIdParam}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`;
};

export const getGoogleReviewsUrl = (card: Card): string => {
  if (card.url) {
    return card.url;
  }

  // Fallback to Google search
  const searchQuery = `${card.customName ?? card.name} ${card.formattedAddress ?? ''}`;
  return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
};

export const getPriceLevel = (priceLevel: number): ReactElement[] => {
  const dollars: ReactElement[] = [];
  for (let i = 0; i < 4; i += 1) {
    const className = i < priceLevel ? 'bold-dollar' : 'light-dollar';
    dollars.push(createElement('span', { key: i, className }, '$'));
  }
  return dollars;
};

export const handleNameKeyDown = (event: KeyboardEvent<HTMLHeadingElement>) => {
  const maxCharacterCount = 200;
  const cardNameHeading = event.target as HTMLHeadingElement;
  const text = cardNameHeading.innerText;

  if (event.key === 'Enter') {
    event.preventDefault(); // Prevent creating a new line
    cardNameHeading.blur(); // This will trigger the onBlur event which calls submitNameChanges
  }

  if (
    text.length >= maxCharacterCount &&
    event.key !== 'Backspace' &&
    event.key !== 'ArrowLeft' &&
    event.key !== 'ArrowRight'
  ) {
    event.preventDefault(); // Prevent further input
  }
};

export const handleNameSubmit = (
  event: FocusEvent<HTMLHeadingElement> | KeyboardEvent<HTMLHeadingElement>,
  card: Card,
  tripId: string,
  dispatch: AppDispatch,
  setSnackbarMessage: (message: { severity: AlertColor; text: string }) => void
) => {
  const cardNameHeading = event.target as HTMLHeadingElement;
  cardNameHeading.setAttribute('contentEditable', 'false');
  const text = cardNameHeading.innerText;

  // Early return if text is not changed
  if (text === (card.customName ?? card.name ?? '')) {
    return;
  }

  if (text.length === 0 || !text.trim()) {
    cardNameHeading.innerText = card.name ?? '';
    if (card.customName) {
      dispatch(updateCard({ tripId, cardId: card.id, field: 'customName', value: null }));
    }
    return;
  }

  dispatch(updateCard({ tripId, cardId: card.id, field: 'customName', value: text }));
  setSnackbarMessage({
    severity: 'success' as AlertColor,
    text: 'Card name updated!',
  });
};

export const handleCoverImageChange = (
  newCoverImage: number,
  tripId: string,
  cardId: string,
  dispatch: AppDispatch,
  handleGalleryClose: () => void,
  setSnackbarMessage: (message: { severity: AlertColor; text: string }) => void
) => {
  dispatch(updateCard({ tripId, cardId, field: 'defaultImage', value: newCoverImage }));
  handleGalleryClose();
  setSnackbarMessage({
    severity: 'success' as AlertColor,
    text: 'Cover photo updated!',
  });
};
